import { useEffect, useState } from "react";
import { Dialog } from "./controls";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isInstalled() {
  return Boolean(window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator as Navigator & { standalone?: boolean }).standalone);
}

export function useFieldAppInstall() {
  const [installed, setInstalled] = useState(isInstalled);
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const display = window.matchMedia?.("(display-mode: standalone)");
    const updateDisplay = () => setInstalled(isInstalled());
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setPrompt(event as InstallPromptEvent);
    };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    display?.addEventListener("change", updateDisplay);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      display?.removeEventListener("change", updateDisplay);
    };
  }, []);

  const install = async () => {
    if (!prompt || installing) return;
    setInstalling(true);
    setError("");
    // Each browser event can be used only once, including after dismissal.
    setPrompt(null);
    try {
      await prompt.prompt();
      await prompt.userChoice;
      // appinstalled confirms completion; accepting a prompt alone does not.
    } catch {
      setError("Use your browser menu to add Workview Pro to your home screen.");
    } finally {
      setInstalling(false);
    }
  };
  return { installed, canInstall: Boolean(prompt), installing, error, install };
}

export function FieldAppInstallDialog({ onClose, installation }: {
  onClose: () => void;
  installation: ReturnType<typeof useFieldAppInstall>;
}) {
  const { canInstall, installing, error, install } = installation;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/.test(navigator.userAgent);
  return (
    <Dialog title="Add to Home Screen" onClose={onClose} width="max-w-sm">
      <div className="space-y-4 text-sm">
        <div className="flex items-center gap-3">
          <img src="/icons/field-192.png" alt="" width={56} height={56} className="rounded-xl border border-slate-200" />
          <div><p className="font-semibold">Workview Pro</p><p className="text-slate-600">Open your Field app with one tap.</p></div>
        </div>
        {canInstall ? <button type="button" disabled={installing} onClick={() => void install()} className="min-h-11 w-full rounded-full bg-[#14263e] px-4 font-semibold text-white disabled:opacity-50">{installing ? "Opening installer…" : "Install Workview Pro"}</button> : null}
        {installing ? <p role="status">Finish in your browser’s install window.</p> : null}
        {error ? <p role="status">{error}</p> : null}
        {ios || !android ? <section aria-label="iPhone installation"><p className="font-semibold">iPhone / iPad</p><ol className="mt-2 list-decimal space-y-2 pl-5"><li>Open this Field app in Safari.</li><li>Tap Share, then Add to Home Screen. Share may be inside the browser menu.</li><li>Keep Open as Web App enabled if shown, then tap Add.</li></ol></section> : null}
        {android || !ios ? <section aria-label="Android installation"><p className="font-semibold">Android</p><ol className="mt-2 list-decimal space-y-2 pl-5"><li>Open this Field app in Chrome.</li><li>Tap the three-dot menu, then Add to Home screen or Install app.</li><li>Confirm Add or Install.</li></ol></section> : null}
        <p className="text-slate-600">Then open Workview Pro from your home screen. Sign in if asked. An internet connection is required.</p>
      </div>
    </Dialog>
  );
}
